import React, { useState, useEffect } from 'react';
import { LoaderCircle, MapPin, Plus, Sparkles, Star, Trash2, Truck, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import UserSectionSidebar from '../components/UserSectionSidebar';
import { useTranslations } from '../translations';
import { fetchUserLocations, deleteUserLocation, setDefaultLocation, searchLocations, addUserLocation, UserLocationsResponse } from '../services/location.api';
import type { Location } from '../services/api.types';
import { getAccessToken, getUserId } from '../services/auth';
import { useNotification } from '../components/NotificationProvider';
import OrderLocationsManager from '../components/OrderLocationsManager';
import ConfirmModal from '../components/ConfirmModal';
import LoadingIndicator from '../components/LoadingIndicator';
import './Locations.css';

interface LocationsProps {
  isLoggedIn: boolean;
  isAuthChecking: boolean;
  isAdmin?: boolean;
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
  onNavigateToHome: () => void;
  onNavigateToProfile: () => void;
  onNavigateToMyAnnouncements?: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToAdministration?: () => void;
  onLogout: () => void;
  onNavigateToCreateProduct?: () => void;
  onNavigateToMessages?: (productId?: string | null) => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToCookies?: () => void;
}

const Locations: React.FC<LocationsProps> = ({
  isLoggedIn,
  isAuthChecking,
  isAdmin = false,
  onNavigateToLogin,
  onNavigateToRegister,
  onNavigateToHome,
  onNavigateToProfile,
  onNavigateToMyAnnouncements,
  onNavigateToSettings,
  onNavigateToAdministration,
  onLogout,
  onNavigateToCreateProduct,
  onNavigateToMessages,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookies
}) => {
  const t = useTranslations();
  const { showNotification } = useNotification();
  const [locations, setLocations] = useState<UserLocationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<string | null>(null);
  const [showAddLocationForm, setShowAddLocationForm] = useState(false);
  const [newLocationSearch, setNewLocationSearch] = useState('');
  const [newLocationOptions, setNewLocationOptions] = useState<Location[]>([]);
  const [selectedNewLocation, setSelectedNewLocation] = useState<Location | null>(null);
  const [showNewLocationDropdown, setShowNewLocationDropdown] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeLocationSection, setActiveLocationSection] = useState<'announcements' | 'orders'>('announcements');
  const [orderLocationCount, setOrderLocationCount] = useState<number | null>(null);

  useEffect(() => {
    const loadUserLocations = async () => {
      if (isAuthChecking) {
        return;
      }
      const token = getAccessToken();
      const userId = getUserId();

      if (!token || !userId) {
        onNavigateToLogin();
        return;
      }

      try {
        const locationsData = await fetchUserLocations(userId, 1, 50);
        setLocations(locationsData);
      } catch (err) {
        console.error('Error fetching user locations:', err);
        const errorMessage = err instanceof Error ? err.message : '';
        if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
          onNavigateToLogin();
          return;
        }
        showNotification('Nu am putut încărca locațiile. Vă rugăm încercați din nou.', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadUserLocations();
  }, [onNavigateToLogin, showNotification, isAuthChecking]);

  useEffect(() => {
    const searchNewLocations = async () => {
      if (!showNewLocationDropdown || newLocationSearch.length === 0) {
        setNewLocationOptions([]);
        return;
      }

      const token = getAccessToken();
      if (!token) {
        return;
      }

      try {
        const response = await searchLocations(1, 20, null, newLocationSearch, token);
        setNewLocationOptions(response.locations.content);
      } catch (error) {
        console.error('Error searching locations:', error);
        const errorMessage = error instanceof Error ? error.message : '';
        if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
          onNavigateToLogin();
          return;
        }
        setNewLocationOptions([]);
      }
    };

    const debounceTimer = setTimeout(() => {
      searchNewLocations();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [newLocationSearch, showNewLocationDropdown, onNavigateToLogin]);

  const handleDeleteClick = (locationId: string) => {
    setLocationToDelete(locationId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!locationToDelete) return;

    const token = getAccessToken();
    if (!token) {
      onNavigateToLogin();
      return;
    }

    try {
      await deleteUserLocation(locationToDelete);
      const userId = getUserId();
      if (userId) {
        const locationsData = await fetchUserLocations(userId, 1, 50);
        setLocations(locationsData);
      }
      showNotification('Locație ștearsă cu succes!', 'success');
      setShowDeleteModal(false);
      setLocationToDelete(null);
    } catch (err) {
      console.error('Error deleting location:', err);
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      showNotification('Nu am putut șterge locația. Vă rugăm încercați din nou.', 'error');
      setShowDeleteModal(false);
      setLocationToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setLocationToDelete(null);
  };

  const handleSetDefault = async (locationId: string) => {
    const token = getAccessToken();
    if (!token) {
      onNavigateToLogin();
      return;
    }

    try {
      await setDefaultLocation(locationId);
      const userId = getUserId();
      if (userId) {
        const locationsData = await fetchUserLocations(userId, 1, 50);
        setLocations(locationsData);
      }
      showNotification('Locație principală setată cu succes!', 'success');
    } catch (err) {
      console.error('Error setting default location:', err);
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      showNotification('Nu am putut seta locația principală. Vă rugăm încercați din nou.', 'error');
    }
  };

  const handleAddLocationClick = () => {
    setShowAddLocationForm(true);
    setNewLocationSearch('');
    setSelectedNewLocation(null);
    setNewLocationOptions([]);
  };

  const handleCancelAddLocation = () => {
    setShowAddLocationForm(false);
    setNewLocationSearch('');
    setSelectedNewLocation(null);
    setNewLocationOptions([]);
  };

  const handleSaveNewLocation = async () => {
    if (!selectedNewLocation) {
      showNotification('Vă rugăm să selectați o locație.', 'error');
      return;
    }

    const token = getAccessToken();
    if (!token) {
      onNavigateToLogin();
      return;
    }

    setIsSaving(true);
    try {
      await addUserLocation(selectedNewLocation.id);
      const userId = getUserId();
      if (userId) {
        const locationsData = await fetchUserLocations(userId, 1, 50);
        setLocations(locationsData);
      }
      setShowAddLocationForm(false);
      setNewLocationSearch('');
      setSelectedNewLocation(null);
      setNewLocationOptions([]);
      showNotification('Locație adăugată cu succes!', 'success');
    } catch (err) {
      console.error('Error adding location:', err);
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage === 'Session expired' || errorMessage === 'Not authenticated') {
        onNavigateToLogin();
        return;
      }
      showNotification('Nu am putut adăuga locația. Vă rugăm încercați din nou.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleNewLocationSelect = (location: Location) => {
    setSelectedNewLocation(location);
    setNewLocationSearch(`${location.diacriticsName || location.name}, jud. ${location.county.name}`);
    setShowNewLocationDropdown(false);
  };

  const primaryLocation = locations?.content.find(loc => loc.defaultLocation);
  const secondaryLocations = locations?.content.filter(loc => !loc.defaultLocation) || [];
  const announcementLocationCount = locations?.totalElements ?? locations?.content.length ?? 0;
  const isOrderLocationsSection = activeLocationSection === 'orders';
  const locationCount = isOrderLocationsSection ? orderLocationCount : announcementLocationCount;
  const displayedLocationCount = locationCount ?? '—';
  const locationCountLabel = isOrderLocationsSection
    ? locationCount === null
      ? t.locations.orderLocations.loading
      : locationCount === 1
        ? t.locations.orderLocations.savedOne
        : t.locations.orderLocations.savedCount.replace('{count}', String(locationCount))
    : locationCount === 1
      ? t.locations.savedOne
      : t.locations.savedCount.replace('{count}', String(locationCount));
  const locationSummaryLabel = isOrderLocationsSection
    ? locationCount === 1
      ? t.locations.orderLocations.savedLabelOne
      : t.locations.orderLocations.savedLabel
    : locationCount === 1
      ? t.locations.savedLabelOne
      : t.locations.savedLabel;

  if (loading) {
    return (
      <div className="locations-page">
        <Header
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          onNavigateToLogin={onNavigateToLogin}
          onNavigateToRegister={onNavigateToRegister}
          onNavigateToHome={onNavigateToHome}
          onLogout={onLogout}
          onProfileClick={onNavigateToProfile}
          onLocationsClick={() => {}}
          onMessagesClick={onNavigateToMessages}
          onAnnouncementsClick={onNavigateToMyAnnouncements}
          onAdministrationClick={onNavigateToAdministration}
          onSettingsClick={onNavigateToSettings}
          onNavigateToCreateProduct={onNavigateToCreateProduct}
        />
        <div className="locations-container">
          <UserSectionSidebar
            activeItem="locations"
            onProfileClick={onNavigateToProfile}
            onMessagesClick={() => onNavigateToMessages?.()}
            onAnnouncementsClick={onNavigateToMyAnnouncements}
            onSettingsClick={onNavigateToSettings}
          />
          <main className="locations-main">
            <h1 className="locations-title">{t.locations.title}</h1>
            <LoadingIndicator message={t.common.loading} variant="page" className="locations-loading" />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="locations-page">
      <Header
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={onNavigateToHome}
        onLogout={onLogout}
        onProfileClick={onNavigateToProfile}
        onLocationsClick={() => {}}
        onMessagesClick={onNavigateToMessages}
        onAnnouncementsClick={onNavigateToMyAnnouncements}
        onAdministrationClick={onNavigateToAdministration}
        onSettingsClick={onNavigateToSettings}
        onNavigateToCreateProduct={onNavigateToCreateProduct}
      />

      <div className="locations-container">
        <UserSectionSidebar
          activeItem="locations"
          onProfileClick={onNavigateToProfile}
          onMessagesClick={() => onNavigateToMessages?.()}
          onAnnouncementsClick={onNavigateToMyAnnouncements}
          onSettingsClick={onNavigateToSettings}
        />

        <main className="locations-main">
          <section className="locations-top-header" aria-labelledby="locations-title">
            <div className="locations-heading">
              <span className="locations-eyebrow">
                <Sparkles size={15} aria-hidden />
                {t.locations.eyebrow}
              </span>
              <h1 id="locations-title" className="locations-title">{t.locations.title}</h1>
              <p className="locations-subtitle">{t.locations.subtitle}</p>
            </div>
            <div className="locations-overview" aria-label={locationCountLabel}>
              <span className="locations-overview-icon">
                {isOrderLocationsSection ? <Truck size={20} aria-hidden /> : <MapPin size={20} aria-hidden />}
              </span>
              <div>
                <strong>{displayedLocationCount}</strong>
                <small>{locationSummaryLabel}</small>
              </div>
            </div>
          </section>

          <section className="locations-workspace">
            <div className="locations-section-tabs" role="tablist" aria-label={t.locations.sectionsLabel}>
            <button
              type="button"
              role="tab"
              aria-selected={activeLocationSection === 'announcements'}
              className={activeLocationSection === 'announcements' ? 'active' : ''}
              onClick={() => setActiveLocationSection('announcements')}
            >
              <MapPin size={17} aria-hidden />
              <span>{t.locations.announcementsTab}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeLocationSection === 'orders'}
              className={activeLocationSection === 'orders' ? 'active' : ''}
              onClick={() => setActiveLocationSection('orders')}
            >
              <Truck size={17} aria-hidden />
              <span>{t.locations.ordersTab}</span>
            </button>
            </div>

          {activeLocationSection === 'announcements' ? (
            <section className="announcement-locations-manager" role="tabpanel">
              <div className="announcement-locations-heading">
                <div className="location-manager-heading-copy">
                  <span className="location-manager-heading-icon"><MapPin size={19} aria-hidden /></span>
                  <div>
                    <div className="location-manager-title-row">
                      <h2>{t.locations.announcementLocationsTitle}</h2>
                      <span className="location-manager-count">{locations?.totalElements ?? locations?.content.length ?? 0}</span>
                    </div>
                    <p>{t.locations.announcementLocationsDescription}</p>
                  </div>
                </div>
                <button type="button" className="announcement-location-add" onClick={handleAddLocationClick}>
                  <Plus size={18} aria-hidden /> {t.locations.addLocation}
                </button>
              </div>

              {showAddLocationForm && (
                <div className="announcement-location-form">
                  <div className="announcement-location-form-heading">
                    <h3>{t.locations.createLocationTitle}</h3>
                    <button type="button" onClick={handleCancelAddLocation} aria-label={t.locations.closeForm}>
                      <X size={19} aria-hidden />
                    </button>
                  </div>
                  <label>
                    <span>{t.locations.location}</span>
                    <div className="add-location-input-wrapper">
                      <div className="location-display-box add-location-input">
                        <MapPin className="announcement-location-input-icon" size={18} aria-hidden />
                        <input
                          type="text"
                          className="add-location-search-input"
                          placeholder={t.locations.searchPlaceholder}
                          value={newLocationSearch}
                          onChange={(event) => setNewLocationSearch(event.target.value)}
                          onFocus={() => setShowNewLocationDropdown(true)}
                          onBlur={() => setTimeout(() => setShowNewLocationDropdown(false), 200)}
                        />
                      </div>
                      {showNewLocationDropdown && newLocationOptions.length > 0 && (
                        <div className="add-location-dropdown">
                          {newLocationOptions.map((location) => (
                            <button
                              type="button"
                              key={location.id}
                              className="add-location-dropdown-item"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => handleNewLocationSelect(location)}
                            >
                              {location.diacriticsName || location.name}, jud. {location.county.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </label>
                  <div className="announcement-location-form-actions">
                    <button type="button" onClick={handleCancelAddLocation} disabled={isSaving}>{t.locations.cancel}</button>
                    <button type="button" className="primary" onClick={handleSaveNewLocation} disabled={isSaving || !selectedNewLocation}>
                      {isSaving && <LoaderCircle className="announcement-location-spinner" size={17} aria-hidden />}
                      {isSaving ? t.locations.saving : t.locations.save}
                    </button>
                  </div>
                </div>
              )}

              {!primaryLocation && secondaryLocations.length === 0 ? (
                <div className="announcement-locations-state">{t.locations.noLocations}</div>
              ) : (
                <div className="announcement-location-list">
                  {[...(primaryLocation ? [primaryLocation] : []), ...secondaryLocations].map((location) => (
                    <article className={`announcement-location-row${location.defaultLocation ? ' default' : ''}`} key={location.id}>
                      <div className="announcement-location-icon"><MapPin size={19} aria-hidden /></div>
                      <div className="announcement-location-details">
                        <div className="announcement-location-name-row">
                          <strong>{location.diacriticsName || location.name}</strong>
                          <span className={location.defaultLocation ? 'primary' : 'secondary'}>
                            {location.defaultLocation ? t.locations.default : t.locations.secondary}
                          </span>
                        </div>
                        <span>jud. {location.county.name}</span>
                      </div>
                      <div className="announcement-location-actions">
                        {!location.defaultLocation && (
                          <button
                            type="button"
                            onClick={() => void handleSetDefault(location.id)}
                            aria-label={`${t.locations.setDefault}: ${location.diacriticsName || location.name}`}
                            title={t.locations.setDefault}
                          >
                            <Star size={17} aria-hidden />
                          </button>
                        )}
                        <button
                          type="button"
                          className="danger"
                          onClick={() => handleDeleteClick(location.id)}
                          aria-label={`${t.common.delete}: ${location.diacriticsName || location.name}`}
                          title={t.common.delete}
                        >
                          <Trash2 size={17} aria-hidden />
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
            ) : <div role="tabpanel"><OrderLocationsManager onLocationCountChange={setOrderLocationCount} /></div>}
          </section>
        </main>
      </div>

      <Footer
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookies={onNavigateToCookies}
      />

      {showDeleteModal && (
        <ConfirmModal
          title={t.locations.deleteTitle}
          message={t.locations.deleteMessage}
          confirmText={t.locations.deleteConfirm}
          cancelText={t.locations.cancel}
          destructive
          onConfirm={() => void handleConfirmDelete()}
          onCancel={handleCancelDelete}
        />
      )}
    </div>
  );
};

export default Locations;
